import { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import { Send, ArrowLeft, Image as ImageIcon, X, Phone, Video } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import UpgradeModal from '@/components/UpgradeModal';
import VideoCall from '@/components/VideoCall';
import { ConnectionItem } from '@/components/ConnectionItem';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Messages = () => {
  const { userId: chatUserId } = useParams();
  const { user, token } = useContext(AuthContext);
  const [connections, setConnections] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [showVideoCall, setShowVideoCall] = useState(false);
  const [callType, setCallType] = useState('video');
  const [isAdminContact, setIsAdminContact] = useState(false);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchConnections();
    axios.get(`${API}/users/admin-contact-status`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => setIsAdminContact(res.data.is_admin_contact)).catch(() => {});
  }, []);

  useEffect(() => {
    if (chatUserId) {
      loadChatWithUser(chatUserId);
    }
  }, [chatUserId]);

  useEffect(() => {
    if (selectedUser) {
      const interval = setInterval(() => {
        fetchMessages(selectedUser.id);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [selectedUser]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConnections = async () => {
    try {
      const response = await axios.get(`${API}/connections`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConnections(response.data);
    } catch (error) {
      console.error('Failed to load connections');
    }
  };

  const loadChatWithUser = async (userId) => {
    try {
      const response = await axios.get(`${API}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedUser(response.data);
      fetchMessages(userId);
    } catch (error) {
      toast.error('Failed to load user');
    }
  };

  const fetchMessages = async (userId) => {
    try {
      const response = await axios.get(`${API}/messages/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to load messages');
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + selectedFiles.length > 3) {
      toast.error('Maximum 3 images allowed per message');
      return;
    }
    
    // Validate file types and sizes
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        toast.error('Only image files are allowed');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} exceeds size limit (5MB)`);
        return;
      }
    }
    
    setSelectedFiles([...selectedFiles, ...files]);
  };

  const removeFile = (index) => {
    setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
  };

  const uploadImages = async () => {
    if (selectedFiles.length === 0) return [];
    
    setUploadingImages(true);
    const formData = new FormData();
    selectedFiles.forEach(file => {
      formData.append('files', file);
    });
    
    try {
      const response = await axios.post(`${API}/upload/media`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      // Extract URLs from media objects
      return response.data.media.map(item => typeof item === 'string' ? item : item.url);
    } catch (error) {
      toast.error('Failed to upload images');
      throw error;
    } finally {
      setUploadingImages(false);
    }
  };

  const handleTyping = () => {
    // Show typing indicator (local state)
    setIsTyping(true);
    
    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Hide typing indicator after 1 second of no typing
    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
    }, 1000);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const sendMessage = async () => {
    if ((!newMessage.trim() && selectedFiles.length === 0) || !selectedUser) return;

    try {
      // Upload images first if any
      let imageUrls = [];
      if (selectedFiles.length > 0) {
        imageUrls = await uploadImages();
      }

      await axios.post(
        `${API}/messages/send`,
        { receiver_id: selectedUser.id, content: newMessage, images: imageUrls },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewMessage('');
      setSelectedFiles([]);
      setIsTyping(false); // Clear typing indicator
      fetchMessages(selectedUser.id);
    } catch (error) {
      // Check if it's a free tier limit error
      if (error.response?.status === 403 && error.response?.data?.detail?.includes('5 messages per day')) {
        setShowUpgradeModal(true);
      } else {
        toast.error('Failed to send message');
      }
    }
  };

  const getOtherUserId = (connection) => {
    return connection.user_id_1 === user.id ? connection.user_id_2 : connection.user_id_1;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-peach-50 to-teal-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 py-8 pt-24">
        <div className="glass-dark rounded-3xl shadow-2xl overflow-hidden" style={{height: '600px'}}>
          <div className="flex h-full">
            {/* Connections List */}
            <div className="w-1/3 border-r border-gray-200 overflow-y-auto">
              <div className="p-4 border-b border-gray-200">
                <h2 className="text-xl font-bold">Messages</h2>
              </div>
              <div data-testid="connections-list">
                {connections.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    <p>No connections yet</p>
                    <Button
                      onClick={() => navigate('/discover')}
                      className="mt-4 bg-coral text-white rounded-full"
                    >
                      Discover People
                    </Button>
                  </div>
                ) : (
                  connections.map((connection) => (
                    <ConnectionItem
                      key={connection.id}
                      connection={connection}
                      userId={getOtherUserId(connection)}
                      isSelected={selectedUser?.id === getOtherUserId(connection)}
                      onClick={() => loadChatWithUser(getOtherUserId(connection))}
                      token={token}
                      isPremium={true}
                      onCall={(u) => {
                        setSelectedUser(u);
                        setCallType('audio');
                        setShowVideoCall(true);
                      }}
                    />
                  ))
                )}
              </div>
            </div>

            {/* Chat Area */}
            <div className="flex-1 flex flex-col">
              {selectedUser ? (
                <>
                  <div className="p-4 border-b border-gray-200 flex items-center space-x-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedUser(null)}
                      className="sm:hidden"
                    >
                      <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <Avatar
                      className="cursor-pointer border-2 border-teal"
                      onClick={() => navigate(`/user/${selectedUser.id}`)}
                    >
                      <AvatarImage src={selectedUser.profile_photo} />
                      <AvatarFallback className="bg-white text-black font-bold">
                        {selectedUser.name.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div
                      className="cursor-pointer flex-1"
                      onClick={() => navigate(`/user/${selectedUser.id}`)}
                    >
                      <h3 className="font-semibold">{selectedUser.name}</h3>
                    </div>
                    {/* Call buttons - Premium or Admin Contact */}
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="rounded-full p-2 hover:bg-teal hover:text-white"
                        onClick={() => {
                          setCallType('audio');
                          setShowVideoCall(true);
                        }}
                        title="Voice Call"
                        data-testid="voice-call-btn"
                      >
                        <Phone className="w-5 h-5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="rounded-full p-2 hover:bg-coral hover:text-white"
                        onClick={() => {
                          setCallType('video');
                          setShowVideoCall(true);
                        }}
                        title="Video Call"
                        data-testid="video-call-btn"
                      >
                        <Video className="w-5 h-5" />
                      </Button>
                    </div>
                  </div>

                  <ScrollArea className="flex-1 p-4" data-testid="messages-area">
                    <div className="space-y-4">
                      {messages.map((message) => (
                        <div
                          key={message.id}
                          data-testid={`message-${message.id}`}
                          className={`flex ${message.sender_id === user.id ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                              message.sender_id === user.id
                                ? 'bg-gradient-to-r from-coral to-peach text-white'
                                : 'bg-white border border-gray-200'
                            }`}
                          >
                            {message.content && (
                              <p className="whitespace-pre-wrap font-medium text-black">
                                {message.content}
                              </p>
                            )}
                            {message.images && message.images.length > 0 && (
                              <div className="mt-2 space-y-2">
                                {message.images.map((img, idx) => (
                                  <img
                                    key={`msg-img-${message.id}-${idx}`}
                                    src={img}
                                    alt="Shared image"
                                    className="rounded-lg max-w-full h-auto cursor-pointer"
                                    onClick={() => window.open(img, '_blank')}
                                  />
                                ))}
                              </div>
                            )}
                            <p className={`text-xs mt-1 ${
                              message.sender_id === user.id ? 'text-gray-400' : 'text-gray-500'
                            }`}>
                              {new Date(message.created_at).toLocaleTimeString()}
                            </p>
                          </div>
                        </div>
                      ))}
                      
                      {/* Typing Indicator */}
                      {isTyping && (
                        <div className="flex justify-start">
                          <div className="bg-gray-200 rounded-2xl px-4 py-3">
                            <div className="flex space-x-1">
                              <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                              <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                              <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Scroll to bottom ref */}
                      <div ref={messagesEndRef} />
                    </div>
                  </ScrollArea>

                  <div className="p-4 border-t border-gray-200">
                    {/* Image file previews */}
                    {selectedFiles.length > 0 && (
                      <div className="mb-3 flex flex-wrap gap-2">
                        {selectedFiles.map((file, idx) => (
                          <div key={`preview-${file.name}-${idx}`} className="relative group">
                            <img 
                              src={URL.createObjectURL(file)} 
                              alt="Preview" 
                              className="h-16 w-16 object-cover rounded-lg" 
                            />
                            <button
                              onClick={() => removeFile(idx)}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    <div className="flex space-x-2">
                      <input
                        type="file"
                        id="message-image-upload"
                        accept="image/*"
                        multiple
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                      <label
                        htmlFor="message-image-upload"
                        className="cursor-pointer inline-flex items-center justify-center px-4 py-2 rounded-full border-2 border-teal hover:bg-teal hover:text-white bg-white text-black"
                      >
                        <ImageIcon className="w-5 h-5" />
                      </label>
                      <Input
                        data-testid="message-input"
                        placeholder="Type a message..."
                        value={newMessage}
                        onChange={(e) => {
                          setNewMessage(e.target.value);
                          handleTyping();
                        }}
                        onKeyPress={handleKeyPress}
                        className="rounded-full"
                      />
                      <Button
                        data-testid="send-message-btn"
                        onClick={sendMessage}
                        disabled={(!newMessage.trim() && selectedFiles.length === 0) || uploadingImages}
                        className="bg-gray-500 hover:bg-gray-600 text-white rounded-full px-6"
                      >
                        {uploadingImages ? '...' : <Send className="w-5 h-5" />}
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-gray-500">
                  <div className="text-center">
                    <p className="text-lg mb-2">Select a conversation</p>
                    <p className="text-sm">Choose someone from your connections to start chatting</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Video Call Modal */}
      <VideoCall
        isOpen={showVideoCall}
        onClose={() => setShowVideoCall(false)}
        remoteUser={selectedUser}
        callType={callType}
      />
      
      {/* Upgrade Modal */}
      <UpgradeModal 
        isOpen={showUpgradeModal} 
        onClose={() => setShowUpgradeModal(false)} 
      />
    </div>
  );
};

export default Messages;